import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail, uploadDocument } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PO_INVOICE_META, PO_PRIORITY_META, PO_RECEIVING_META, PO_SHIPMENT_META, PO_STATUS_META, PoBadge } from "@/lib/poStatus";
import { toast } from "sonner";
import { ArrowUpDown, FileText, History, Loader2, MoreHorizontal, Pencil, Plus, Printer, RotateCcw, Scissors, Search, Send, Sparkles, Trash2, Truck, X, ZoomIn, ZoomOut } from "lucide-react";
import { printPurchaseOrder } from "@/lib/purchaseOrderPrint";
import { PoDialog } from "@/components/po/PoDialog";
import { PoShipmentDialog } from "@/components/po/PoShipmentDialog";
import { PoReceiveDialog } from "@/components/po/PoReceiveDialog";
import PoInvoiceDialog from "@/components/po/PoInvoiceDialog";
import InvoiceReviewDialog from "@/components/po/InvoiceReviewDialog";
import {
  PoCarrierCell, PoCostCell, PoDatesCell, PoDocsCell, PoFilesCell, PoFulfillmentCell, PoInvoicesCell, PoOrderCell,
  PoPhotosCell, PoPickupDelivery, PoPriorityCell, PoReceivingCell, PoShipmentsCell, PoTextCell, money,
} from "@/components/po/PoCells";

const num = (n) => Math.round(n).toLocaleString();
const today = () => new Date().toISOString().slice(0, 10);

function PoStatusSelect({ po, onChanged }) {
  const change = async (v) => {
    try {
      await api.put(`/purchase-orders/${po.id}/status`, { status: v });
      toast.success(`${po.po_number} → ${PO_STATUS_META[v]?.label || v}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update status");
    }
  };
  const options = {
    draft: [["open", "Issue (Open)"], ["cancelled", "Cancel"]],
    open: [["ready", "Ready (supplier confirmed)"], ["draft", "Back to Draft"], ["cancelled", "Cancel"]],
    ready: [["open", "Back to Open"], ["cancelled", "Cancel"]],
    cancelled: [["open", "Reopen"]],
    received: po.closed_short ? [["open", "Reopen"]] : [],
  }[po.status] || [];
  if (!options.length) return <PoBadge meta={PO_STATUS_META[po.status]} testId={`po-status-badge-${po.id}`} />;
  return (
    <Select value={po.status} onValueChange={change}>
      <SelectTrigger className="h-auto w-auto gap-1 rounded-full border-0 p-0 shadow-none" data-testid={`po-status-select-${po.id}`}>
        <PoBadge meta={PO_STATUS_META[po.status]} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={po.status} disabled>{PO_STATUS_META[po.status]?.label}</SelectItem>
        {options.map(([v, l]) => (<SelectItem key={v} value={v}>{l}</SelectItem>))}
      </SelectContent>
    </Select>
  );
}

export default function PurchaseOrders() {
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [components, setComponents] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [fPerf, setFPerf] = useState("all");
  const [fSupplier, setFSupplier] = useState("all");
  const [fComponent, setFComponent] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fPriority, setFPriority] = useState("all");
  const [fPickup, setFPickup] = useState("all");
  const [fShipment, setFShipment] = useState("all");
  const [fReceiving, setFReceiving] = useState("all");
  const [fPayment, setFPayment] = useState("all");
  const [fDueBy, setFDueBy] = useState("");
  const [sort, setSort] = useState({ key: "po_date", dir: "desc" });
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [poCreating, setPoCreating] = useState(false);
  const [poDragOver, setPoDragOver] = useState(false);
  const createFromPo = async (f) => {
    setPoCreating(true);
    toast.info(`Reading ${f.name} and building the purchase order…`);
    try {
      const d = await uploadDocument(f);
      const { data } = await api.post("/purchase-orders/from-po", { path: d.path, name: d.name || f.name, url: d.url });
      toast.success(`${data.po_number} created — ${data.supplier_name}, ${data.lines.length} line${data.lines.length !== 1 ? "s" : ""}`, { duration: 10000 });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't create a purchase order from that PO");
    } finally { setPoCreating(false); }
  };
  const [invParsing, setInvParsing] = useState(false);
  const [invDragOver, setInvDragOver] = useState(false);
  const [invReview, setInvReview] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const runBackfill = async () => {
    setBackfilling(true);
    const tally = { updated: 0, no_lines: 0, failed: 0 };
    try {
      for (let i = 0; i < 15; i++) {
        const { data } = await api.post("/purchase-orders/backfill-invoice-costs");
        tally.updated += data.updated; tally.no_lines += data.no_lines; tally.failed += data.failed;
        if (data.scanned === 0 && i === 0) { toast.info("No attached invoices left to backfill — all invoice PDFs are already costed or have no attachment"); return; }
        if (data.remaining === 0) break;
        toast.info(`Backfilling… ${data.remaining} invoice${data.remaining !== 1 ? "s" : ""} left`);
      }
      const parts = [`${tally.updated} costed`];
      if (tally.no_lines) parts.push(`${tally.no_lines} skipped`);
      if (tally.failed) parts.push(`${tally.failed} failed`);
      (tally.updated > 0 ? toast.success : toast.warning)(`Backfill finished: ${parts.join(", ")} — averages updated`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Backfill failed");
    } finally { setBackfilling(false); }
  };
  const parseInvoice = async (f) => {
    setInvParsing(true);
    toast.info(`Reading invoice ${f.name}…`);
    try {
      const d = await uploadDocument(f);
      const { data } = await api.post("/purchase-orders/parse-invoice", { path: d.path, name: d.name || f.name, url: d.url });
      setInvReview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't read that invoice");
    } finally { setInvParsing(false); }
  };
  const [shipDialog, setShipDialog] = useState(null);
  const [recvDialog, setRecvDialog] = useState(null);
  const [invDialog, setInvDialog] = useState(null);
  const [delPo, setDelPo] = useState(null);
  const [closeShortPo, setCloseShortPo] = useState(null);
  const [reverseTarget, setReverseTarget] = useState(null);
  const companyRef = useRef(null);

  const printPO = async (po) => {
    if (!companyRef.current) {
      try { companyRef.current = (await api.get("/company-settings")).data; } catch { companyRef.current = {}; }
    }
    printPurchaseOrder(po, companyRef.current, suppliers.find((s) => s.id === po.supplier_id) || null);
  };

  const load = async () => {
    try {
      const [p, s, c, lf] = await Promise.all([
        api.get("/purchase-orders"), api.get("/suppliers"), api.get("/components"),
        api.get("/land-freight").catch(() => ({ data: [] })),
      ]);
      setPos(p.data);
      setSuppliers(s.data);
      setComponents(c.data.filter((x) => (x.types || []).length));
      setCarriers((lf.data || []).map((x) => x.company_name).filter(Boolean));
    } catch {
      toast.error("Failed to load purchase orders");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    (async () => {
      try {
        const [cs, cps] = await Promise.all([api.get("/company-settings"), api.get("/co-packers")]);
        const opts = [];
        const plant = cs.data?.company_name || cs.data?.bottler_name;
        if (plant) opts.push({ group: "Plant", name: plant, address: [cs.data?.address_line1, cs.data?.address_line2].filter(Boolean).join(", ") });
        (cps.data || []).forEach((c) => c.company_name && opts.push({ group: "Co-Packer", name: c.company_name, address: c.address || "" }));
        setDestinations(opts);
      } catch { /* optional */ }
    })();
  }, []);

  const t = today();
  const isPast = (po) => ["open", "ready"].includes(po.status) && po.rdd && po.rdd < t && po.total_received < po.total_ordered;
  const isRisk = (po) => ["open", "ready"].includes(po.status) && po.edd && po.rdd && po.edd > po.rdd;
  const isOnTime = (po) => ["open", "ready"].includes(po.status) && !isPast(po) && !isRisk(po);

  const counts = useMemo(() => ({
    all: pos.length,
    draft: pos.filter((p) => p.status === "draft").length,
    open: pos.filter((p) => p.status === "open").length,
    ready: pos.filter((p) => p.status === "ready").length,
    received: pos.filter((p) => p.status === "received").length,
    cancelled: pos.filter((p) => p.status === "cancelled").length,
    onTime: pos.filter(isOnTime).length,
    atRisk: pos.filter(isRisk).length,
    pastDue: pos.filter(isPast).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [pos, t]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return pos.filter((po) => {
      if (tab !== "all" && po.status !== tab) return false;
      if (fPerf === "on_time" && !isOnTime(po)) return false;
      if (fPerf === "at_risk" && !isRisk(po)) return false;
      if (fPerf === "past_due" && !isPast(po)) return false;
      if (fSupplier !== "all" && po.supplier_id !== fSupplier) return false;
      if (fComponent !== "all" && !po.lines.some((l) => l.component_id === fComponent)) return false;
      if (fStatus !== "all" && po.status !== fStatus) return false;
      if (fPriority !== "all" && String(po.priority || 3) !== fPriority) return false;
      if (fPickup !== "all" && (po.ship_method || "unset") !== fPickup) return false;
      if (fShipment !== "all") {
        const stats = (po.shipments || []).map((s) => s.status);
        if (fShipment === "none" ? stats.length > 0 : !stats.includes(fShipment)) return false;
      }
      if (fReceiving !== "all" && po.receiving_status !== fReceiving) return false;
      if (fPayment !== "all" && (po.invoice_status || "not_invoiced") !== fPayment) return false;
      if (fDueBy && (!po.rdd || po.rdd > fDueBy)) return false;
      if (term) {
        const hay = [po.po_number, po.supplier_name, po.supplier_ref, po.tracking, po.carrier,
          ...(po.shipments || []).map((s) => `${s.shipment_number} ${s.tracking}`),
          ...(po.invoices || []).map((i) => i.invoice_number),
          ...po.lines.map((l) => `${l.name} ${l.sku} ${l.description}`)].join(" ").toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos, tab, fPerf, fSupplier, fComponent, fStatus, fPriority, fPickup, fShipment, fReceiving, fPayment, fDueBy, q, t]);

  const metrics = useMemo(() => {
    let total = 0, outstanding = 0, open = 0, awaiting = 0, pastDue = 0, discrepancies = 0;
    for (const po of filtered) {
      if (po.status === "cancelled") continue;
      total += po.total || 0;
      if (["open", "ready"].includes(po.status)) {
        outstanding += po.outstanding_value || 0;
        open += 1;
        if (po.total_received < po.total_ordered) awaiting += 1;
        if (isPast(po)) pastDue += 1;
      }
      if (po.has_discrepancy) discrepancies += 1;
    }
    return { total, outstanding, open, awaiting, pastDue, discrepancies };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, t]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sort.dir === "asc" ? 1 : -1;
    const by = {
      po_date: (a, b) => (a.order_date || "").localeCompare(b.order_date || ""),
      rdd: (a, b) => (a.rdd || "9999").localeCompare(b.rdd || "9999"),
      edd: (a, b) => (a.edd || "9999").localeCompare(b.edd || "9999"),
      supplier: (a, b) => (a.supplier_name || "").localeCompare(b.supplier_name || ""),
      value: (a, b) => (a.total || 0) - (b.total || 0),
      priority: (a, b) => (a.priority || 3) - (b.priority || 3),
      receiving: (a, b) => ((a.total_received / (a.total_ordered || 1)) - (b.total_received / (b.total_ordered || 1))),
    }[sort.key] || ((a, b) => 0);
    arr.sort((a, b) => dir * by(a, b) || (b.po_num_int || 0) - (a.po_num_int || 0) || (a.po_number || "").localeCompare(b.po_number || ""));
    return arr;
  }, [filtered, sort]);

  const PAGE_SIZE = 20;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [tab, q, fSupplier, fComponent, fStatus, fPriority, fPickup, fShipment, fReceiving, fPayment, fDueBy, fPerf, sort]);
  const visibleRows = useMemo(() => (sorted.length > visibleCount ? sorted.slice(0, visibleCount) : sorted), [sorted, visibleCount]);
  const showMoreBar = sorted.length > visibleCount ? (
    <div className="flex items-center justify-center gap-3 border border-t-0 border-slate-200 bg-slate-50 py-2.5" data-testid="po-show-more-bar">
      <span className="text-xs tabular-nums text-slate-500">Showing {visibleRows.length} of {sorted.length} orders</span>
      <button type="button" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
        className="rounded-sm border border-[#002FA7] px-3 py-1 text-xs font-semibold text-[#002FA7] hover:bg-[#002FA7]/5"
        data-testid="po-show-more">Show {Math.min(PAGE_SIZE, sorted.length - visibleCount)} more</button>
      <button type="button" onClick={() => setVisibleCount(sorted.length)}
        className="text-xs font-semibold text-slate-500 underline-offset-2 hover:text-[#002FA7] hover:underline"
        data-testid="po-show-all">Show all</button>
    </div>
  ) : null;

  const tableWrapRef = useRef(null);
  const tableRef = useRef(null);
  const [fitZoom, setFitZoom] = useState(1);
  const [zoomMode, setZoomMode] = useState("fit");
  const [manualZoom, setManualZoom] = useState(1);
  const tableZoom = zoomMode === "fit" ? fitZoom : manualZoom;
  useLayoutEffect(() => {
    const calc = () => {
      const wrap = tableWrapRef.current;
      const tbl = tableRef.current;
      if (!wrap || !tbl) return;
      tbl.style.zoom = 1;
      const natural = tbl.offsetWidth;
      if (!natural) return;
      let z = Math.min(1, Math.max(0.55, Math.floor((wrap.clientWidth / natural) * 100) / 100));
      tbl.style.zoom = z;
      let guard = 20;
      while (guard-- > 0 && z > 0.55 && wrap.scrollWidth > wrap.clientWidth + 1) {
        z = Math.round((z - 0.01) * 100) / 100;
        tbl.style.zoom = z;
      }
      setFitZoom(z);
      tbl.style.zoom = zoomMode === "fit" ? z : manualZoom;
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [visibleRows, loading, zoomMode, manualZoom]);
  const stepZoom = (d) => { setZoomMode("manual"); setManualZoom(Math.min(1.5, Math.max(0.55, Math.round((tableZoom + d) * 20) / 20))); };

  const hasFilters = fSupplier !== "all" || fComponent !== "all" || fStatus !== "all" || fPriority !== "all" ||
    fPickup !== "all" || fShipment !== "all" || fReceiving !== "all" || fPayment !== "all" || !!fDueBy || !!q.trim() || fPerf !== "all";
  const clearFilters = () => {
    setFSupplier("all"); setFComponent("all"); setFStatus("all"); setFPriority("all");
    setFPickup("all"); setFShipment("all"); setFReceiving("all"); setFPayment("all"); setFDueBy(""); setQ(""); setFPerf("all");
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/purchase-orders/${delPo.id}`);
      toast.success(`${delPo.po_number} deleted`);
      setDelPo(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to delete");
    }
  };

  const confirmCloseShort = async () => {
    try {
      await api.post(`/purchase-orders/${closeShortPo.id}/close-short`);
      toast.success(`${closeShortPo.po_number} closed short — remaining quantities removed from On Order`);
      setCloseShortPo(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to close short");
    }
  };

  const confirmReverse = async () => {
    try {
      await api.post(`/po-receipts/${reverseTarget.receipt.id}/reverse`);
      toast.success(`${reverseTarget.receipt.receipt_number} reversed — inventory adjusted back`);
      setReverseTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to reverse receipt");
    }
  };

  const duplicatePo = (po) => {
    setDialog({
      open: true,
      initial: { ...po, id: null, po_number: "", status: "open", lines: po.lines.map((l) => ({ ...l, id: "", received: 0, confirmed_qty: 0 })) },
    });
  };

  const emailPo = (po) => {
    const sup = suppliers.find((s) => s.id === po.supplier_id);
    const emails = (sup?.contacts || []).filter((c) => !c.suppress_email).map((c) => c.email).filter(Boolean).join(";");
    const subject = `Purchase Order ${po.po_number}${companyRef.current?.company_name ? ` — ${companyRef.current.company_name}` : ""}`;
    const body = `Hello,%0D%0A%0D%0APlease find our purchase order ${po.po_number}.%0D%0A` +
      po.lines.map((l) => `- ${l.name}: ${num(l.quantity)} ${l.unit}`).join("%0D%0A") +
      `%0D%0A%0D%0ARequired delivery: ${po.rdd || "TBD"}%0D%0A%0D%0APlease confirm receipt and delivery dates.%0D%0A`;
    window.location.href = `mailto:${emails}?subject=${encodeURIComponent(subject)}&body=${body}`;
    toast.info("Email draft opened — attach the printed PO PDF before sending");
  };

  const sortBtn = (key, label) => (
    <button key={key} type="button"
      onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }))}
      className={`inline-flex items-center gap-1 ${sort.key === key ? "text-[#002FA7]" : "hover:text-slate-700"}`}
      data-testid={`po-sort-${key}`}>
      {label} <ArrowUpDown className="h-3 w-3" />
    </button>
  );

  const TABS = [["all", "All", "all"], ["open", "Open", "open"], ["ready", "Ready", "ready"], ["received", "Received", "received"], ["cancelled", "Cancelled", "cancelled"]];
  const PERF_TABS = [["all", "All", null], ["on_time", "On Time", "onTime"], ["at_risk", "At Risk", "atRisk"], ["past_due", "Past Due", "pastDue"]];

  const rowActions = (po) => (
    <div className="flex items-center justify-end gap-1">
      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-[#002FA7]" title="Edit" onClick={() => setDialog({ open: true, initial: po })} data-testid={`po-edit-${po.id}`}><Pencil className="h-4 w-4" /></Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-[#002FA7]" title="Print purchase order" onClick={() => printPO(po)} data-testid={`po-print-${po.id}`}><Printer className="h-4 w-4" /></Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-[#002FA7]" data-testid={`po-more-${po.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-sm">
          {["open", "ready"].includes(po.status) && (
            <>
              <DropdownMenuItem onClick={() => setShipDialog({ po, shipment: null })} data-testid={`po-action-shipment-${po.id}`}><Truck className="mr-2 h-3.5 w-3.5" /> Add Inbound Shipment</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setRecvDialog({ po, shipment: null })} data-testid={`po-action-receive-${po.id}`}><Plus className="mr-2 h-3.5 w-3.5" /> Receive Shipment</DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={() => duplicatePo(po)} data-testid={`po-action-duplicate-${po.id}`}><FileText className="mr-2 h-3.5 w-3.5" /> Duplicate</DropdownMenuItem>
          <DropdownMenuItem onClick={() => emailPo(po)} data-testid={`po-action-email-${po.id}`}><Send className="mr-2 h-3.5 w-3.5" /> Email to Supplier</DropdownMenuItem>
          {["open", "ready"].includes(po.status) && po.total_received > 0 && po.total_received < po.total_ordered && (
            <DropdownMenuItem onClick={() => setCloseShortPo(po)} data-testid={`po-action-close-short-${po.id}`}><Scissors className="mr-2 h-3.5 w-3.5" /> Close Short</DropdownMenuItem>
          )}
          {po.status === "cancelled" && (
            <DropdownMenuItem onClick={async () => { try { await api.put(`/purchase-orders/${po.id}/status`, { status: "open" }); toast.success("Reopened"); load(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } }} data-testid={`po-action-reopen-${po.id}`}>
              <RotateCcw className="mr-2 h-3.5 w-3.5" /> Reopen
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={() => setDelPo(po)} data-testid={`po-delete-${po.id}`}><Trash2 className="mr-2 h-3.5 w-3.5" /> Delete</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <main className="mx-auto max-w-full px-4 py-8 sm:px-6 md:px-8 md:py-10" data-testid="purchase-orders-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Operations</p>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">Inventory Purchasing</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setPoDragOver(true); }}
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setPoDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setPoDragOver(false); }}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation(); setPoDragOver(false);
              if (poCreating) return;
              const f = e.dataTransfer?.files?.[0];
              if (f) createFromPo(f);
              else toast.error("Drop a file — dragging directly from some email previews isn't supported; save the attachment first");
            }}
            className={`inline-flex cursor-pointer items-center rounded-sm border px-4 py-2 text-sm font-semibold transition-colors ${poDragOver ? "border-dashed border-[#002FA7] bg-blue-50 text-[#002FA7]" : "border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5"} ${poCreating ? "pointer-events-none opacity-60" : ""}`}
            data-testid="new-po-from-doc-button" title="Upload or drag a PO you generated for a supplier — a Purchase Order will be created from it automatically">
            {poCreating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4 text-amber-500" />}
            {poCreating ? "Reading PO…" : poDragOver ? "Drop PO here" : "New Order from PO"}
            <input type="file" accept=".pdf,.xlsx,.png,.jpg,.jpeg,.txt,.csv" className="hidden" disabled={poCreating}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) createFromPo(f); }} />
          </label>
          <button type="button" onClick={runBackfill} disabled={backfilling}
            className="inline-flex items-center rounded-sm border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-60"
            data-testid="backfill-costs-button" title="Re-read the invoice PDFs already attached to past POs so historical purchases count toward component average costs">
            {backfilling ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <History className="mr-1.5 h-4 w-4 text-[#002FA7]" />}
            {backfilling ? "Backfilling…" : "Backfill Costs"}
          </button>
          <label
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setInvDragOver(true); }}
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setInvDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setInvDragOver(false); }}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation(); setInvDragOver(false);
              if (invParsing) return;
              const f = e.dataTransfer?.files?.[0];
              if (f) parseInvoice(f);
              else toast.error("Drop a file — save the attachment first if dragging from an email preview");
            }}
            className={`inline-flex cursor-pointer items-center rounded-sm border px-4 py-2 text-sm font-semibold transition-colors ${invDragOver ? "border-dashed border-[#002FA7] bg-blue-50 text-[#002FA7]" : "border-slate-300 text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]"} ${invParsing ? "pointer-events-none opacity-60" : ""}`}
            data-testid="upload-invoice-button" title="Upload or drag a supplier invoice PDF — it's read, matched to a PO and filed after your review">
            {invParsing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileText className="mr-1.5 h-4 w-4 text-[#002FA7]" />}
            {invParsing ? "Reading invoice…" : invDragOver ? "Drop invoice here" : "Upload Invoice"}
            <input type="file" accept=".pdf,.xlsx,.png,.jpg,.jpeg,.txt,.csv" className="hidden" disabled={invParsing}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) parseInvoice(f); }} />
          </label>
          <Button onClick={() => setDialog({ open: true, initial: null })} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="new-po-button">
            <Plus className="mr-1.5 h-4 w-4" /> New Purchase Order
          </Button>
        </div>
      </div>

      {/* Tabs + search */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="inline-flex flex-wrap rounded-sm border border-slate-200 bg-slate-50 p-1" data-testid="po-tabs">
          {TABS.map(([k, lbl, ck]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={`rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${tab === k ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              data-testid={`po-tab-${k}`}>
              {lbl} <span className="ml-1 text-xs font-normal text-slate-400">{counts[ck]}</span>
            </button>
          ))}
        </div>
        <div className="inline-flex rounded-sm border border-slate-200 bg-slate-50 p-1" data-testid="po-perf-tabs">
          {PERF_TABS.map(([k, lbl, ck]) => (
            <button key={k} type="button" onClick={() => setFPerf(k)}
              className={`rounded-sm px-3 py-1.5 text-xs font-semibold transition-colors ${fPerf === k ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              data-testid={`po-perf-${k}`}>
              {lbl}{ck ? <span className="ml-1 text-xs font-normal text-slate-400">{counts[ck]}</span> : null}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Purchase Orders"
            className="h-9 w-52 rounded-sm border-slate-200 pl-8 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-[#002FA7]" data-testid="po-search" />
          {q && (
            <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="po-search-clear">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Secondary filters */}
      <div className="mb-5 mt-3 flex flex-wrap items-center gap-2">
        <Select value={fSupplier} onValueChange={setFSupplier}>
          <SelectTrigger className="h-9 w-[150px] rounded-sm text-xs" data-testid="po-filter-supplier"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All suppliers</SelectItem>
            {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={fComponent} onValueChange={setFComponent}>
          <SelectTrigger className="h-9 w-[150px] rounded-sm text-xs" data-testid="po-filter-component"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All components</SelectItem>
            {components.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="h-9 w-[150px] rounded-sm text-xs" data-testid="po-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All PO statuses</SelectItem>
            {["draft", "open", "ready", "received", "cancelled"].map((k) => (<SelectItem key={k} value={k}>{PO_STATUS_META[k].label}{k === "draft" ? ` (${counts.draft})` : ""}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={fPriority} onValueChange={setFPriority}>
          <SelectTrigger className="h-9 w-[130px] rounded-sm text-xs" data-testid="po-filter-priority"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {[1, 2, 3, 4].map((k) => (<SelectItem key={k} value={String(k)}>{PO_PRIORITY_META[k].label}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={fPickup} onValueChange={setFPickup}>
          <SelectTrigger className="h-9 w-[150px] rounded-sm text-xs" data-testid="po-filter-pickup"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Pickup &amp; Delivery</SelectItem>
            <SelectItem value="pickup">We pick up</SelectItem>
            <SelectItem value="delivery">Supplier delivers</SelectItem>
            <SelectItem value="unset">Not set</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fShipment} onValueChange={setFShipment}>
          <SelectTrigger className={`h-9 w-[150px] rounded-sm text-xs ${fShipment !== "all" ? "border-[#002FA7] text-[#002FA7]" : ""}`} data-testid="po-filter-shipment"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Shipment: All</SelectItem>
            <SelectItem value="none">No shipments yet</SelectItem>
            {Object.entries(PO_SHIPMENT_META).map(([k, m]) => (<SelectItem key={k} value={k}>{m.label}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={fReceiving} onValueChange={setFReceiving}>
          <SelectTrigger className={`h-9 w-[160px] rounded-sm text-xs ${fReceiving !== "all" ? "border-[#002FA7] text-[#002FA7]" : ""}`} data-testid="po-filter-receiving"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Receiving: All</SelectItem>
            {Object.entries(PO_RECEIVING_META).map(([k, m]) => (<SelectItem key={k} value={k}>{m.label}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={fPayment} onValueChange={setFPayment}>
          <SelectTrigger className={`h-9 w-[150px] rounded-sm text-xs ${fPayment !== "all" ? "border-[#002FA7] text-[#002FA7]" : ""}`} data-testid="po-filter-payment"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Payment: All</SelectItem>
            {Object.entries(PO_INVOICE_META).map(([k, m]) => (<SelectItem key={k} value={k}>{m.label}</SelectItem>))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Due by</span>
          <Input type="date" value={fDueBy} onChange={(e) => setFDueBy(e.target.value)} className="h-9 w-[140px] rounded-sm text-xs" data-testid="po-filter-due-by" />
          {fDueBy && <button type="button" onClick={() => setFDueBy("")} className="text-slate-400 hover:text-slate-700" data-testid="po-filter-due-clear"><X className="h-3.5 w-3.5" /></button>}
        </div>
        {hasFilters && (
          <button type="button" onClick={clearFilters} className="inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline" data-testid="po-clear-filters">
            <X className="h-3 w-3" /> Clear Filters
          </button>
        )}
        <div className="ml-auto flex items-center gap-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          <div className="hidden items-center gap-1 md:flex" data-testid="po-zoom-controls">
            <button type="button" onClick={() => stepZoom(-0.05)} title="Zoom out" className="p-1 hover:text-[#002FA7]" data-testid="po-zoom-out"><ZoomOut className="h-3.5 w-3.5" /></button>
            <span className="w-9 text-center tabular-nums" data-testid="po-zoom-level">{Math.round(tableZoom * 100)}%</span>
            <button type="button" onClick={() => stepZoom(0.05)} title="Zoom in" className="p-1 hover:text-[#002FA7]" data-testid="po-zoom-in"><ZoomIn className="h-3.5 w-3.5" /></button>
            {zoomMode !== "fit" && (
              <button type="button" onClick={() => setZoomMode("fit")} className="ml-0.5 font-semibold text-[#002FA7] hover:underline" data-testid="po-zoom-fit">Fit</button>
            )}
          </div>
          <span>Sort:</span>
          {sortBtn("po_date", "PO Date")}
          {sortBtn("rdd", "RDD")}
          {sortBtn("edd", "EDD")}
          {sortBtn("supplier", "Supplier")}
          {sortBtn("value", "Value")}
          {sortBtn("priority", "Priority")}
          {sortBtn("receiving", "Received %")}
        </div>
      </div>

      {/* Summary metrics */}
      {!loading && filtered.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-1 border border-slate-200 bg-slate-50 px-4 py-2.5" data-testid="po-summary">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{hasFilters || tab !== "all" ? "Filtered POs" : "All POs"}</span>
          <span className="font-heading text-lg font-black tabular-nums tracking-tight text-slate-900" data-testid="po-summary-total">{money(metrics.total)}</span>
          <span className="text-xs text-slate-500">total PO value</span>
          <span className="font-semibold tabular-nums text-amber-700" data-testid="po-summary-outstanding">{money(metrics.outstanding)}</span>
          <span className="text-xs text-slate-500">outstanding</span>
          <span className="text-xs tabular-nums text-slate-500"><b className="text-slate-800">{metrics.open}</b> open</span>
          <span className="text-xs tabular-nums text-slate-500"><b className="text-slate-800">{metrics.awaiting}</b> awaiting receipt</span>
          <span className={`text-xs tabular-nums ${metrics.pastDue ? "font-semibold text-red-600" : "text-slate-500"}`} data-testid="po-summary-past-due"><b>{metrics.pastDue}</b> past due</span>
          <span className={`text-xs tabular-nums ${metrics.discrepancies ? "font-semibold text-red-600" : "text-slate-500"}`}><b>{metrics.discrepancies}</b> with discrepancies</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
      ) : sorted.length === 0 ? (
        <div className="mt-6 flex flex-col items-center border border-dashed border-slate-300 px-6 py-16 text-center" data-testid="po-empty-state">
          <FileText className="h-10 w-10 text-slate-300" strokeWidth={1.5} />
          <h2 className="mt-4 font-heading text-xl font-bold tracking-tight text-slate-900">{hasFilters || tab !== "all" ? "No matching purchase orders." : "No purchase orders yet"}</h2>
          <p className="mt-2 max-w-md text-sm text-slate-500">Issue a PO to start tracking incoming components — they'll appear as On Order in the inventory until received.</p>
        </div>
      ) : (
        <div ref={tableWrapRef} className="max-h-[calc(100vh-140px)] overflow-auto border border-slate-200" data-testid="po-table">
          <table ref={tableRef} style={{ zoom: tableZoom }} className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50 shadow-[0_1px_0_0_#e2e8f0]">
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                <th className="px-3 py-3">Supplier</th>
                <th className="border-l border-slate-200 px-3 py-3">Dates</th>
                <th className="border-l border-slate-200 px-3 py-3">PO #</th>
                <th className="border-l border-slate-200 px-3 py-3">Order</th>
                <th className="border-l border-slate-200 px-3 py-3">Cost</th>
                <th className="border-l border-slate-200 px-3 py-3">Fulfillment</th>
                <th className="border-l border-slate-200 px-3 py-3">Pickup / Delivery</th>
                <th className="border-l border-slate-200 px-3 py-3">Origin</th>
                <th className="border-l border-slate-200 px-3 py-3 min-w-[150px]">Destination</th>
                <th className="border-l border-slate-200 px-3 py-3">Shipping Docs</th>
                <th className="border-l border-slate-200 px-3 py-3">Carrier</th>
                <th className="border-l border-slate-200 px-3 py-3">Load Photos</th>
                <th className="border-l border-slate-200 px-3 py-3">Shipment Status</th>
                <th className="border-l border-slate-200 px-3 py-3">Receiving</th>
                <th className="border-l border-slate-200 px-3 py-3">Supplier Invoices</th>
                <th className="border-l border-slate-200 px-3 py-3">Priority</th>
                <th className="w-28 border-l border-slate-200 px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((po) => {
                const dim = ["cancelled", "received"].includes(po.status) ? "opacity-70" : "";
                return (
                  <tr key={po.id} className={`border-b border-slate-100 hover:bg-slate-50/60 ${dim}`} data-testid={`po-row-${po.id}`}>
                    <td className="px-3 py-3 font-medium text-slate-800">
                      {po.supplier_name}
                      {po.supplier_ref && <p className="text-[10px] font-normal text-slate-400">Ref {po.supplier_ref}</p>}
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3"><PoDatesCell po={po} onChanged={load} todayIso={t} /></td>
                    <td className="border-l border-slate-100 px-3 py-3" data-testid={`po-number-cell-${po.id}`}>
                      <div className="flex items-center gap-1.5">
                        <p className="font-semibold text-[#002FA7]" data-testid={`po-number-${po.id}`}>{po.po_number}</p>
                        <PoFilesCell po={po} onChanged={load} />
                      </div>
                      <div className="mt-1" onClick={(e) => e.stopPropagation()}><PoStatusSelect po={po} onChanged={load} /></div>
                      {po.closed_short && <p className="mt-0.5 text-[9px] font-semibold uppercase text-amber-600">Closed short</p>}
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3"><PoOrderCell po={po} /></td>
                    <td className="border-l border-slate-100 px-3 py-3"><PoCostCell po={po} /></td>
                    <td className="border-l border-slate-100 px-3 py-3"><PoFulfillmentCell po={po} onChanged={load} /></td>
                    <td className="border-l border-slate-100 px-3 py-3"><PoPickupDelivery po={po} onChanged={load} /></td>
                    <td className="border-l border-slate-100 px-3 py-3"><PoTextCell po={po} field="origin" onChanged={load} placeholder="Set origin" testId={`po-origin-${po.id}`} multiline /></td>
                    <td className="border-l border-slate-100 px-3 py-3"><PoTextCell po={po} field="destination" onChanged={load} placeholder="Set destination" testId={`po-destination-${po.id}`} multiline /></td>
                    <td className="border-l border-slate-100 px-3 py-3"><PoDocsCell po={po} onChanged={load} /></td>
                    <td className="border-l border-slate-100 px-3 py-3"><PoCarrierCell po={po} carriers={carriers} onChanged={load} /></td>
                    <td className="border-l border-slate-100 px-3 py-3"><PoPhotosCell po={po} onChanged={load} /></td>
                    <td className="border-l border-slate-100 px-3 py-3">
                      <PoShipmentsCell po={po} onChanged={load}
                        onAddShipment={(p) => setShipDialog({ po: p, shipment: null })}
                        onEditShipment={(p, s) => setShipDialog({ po: p, shipment: s })}
                        onReceiveShipment={(p, s) => setRecvDialog({ po: p, shipment: s })} />
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3">
                      <PoReceivingCell po={po} onReceive={(p, s) => setRecvDialog({ po: p, shipment: s })} onReverseReceipt={(p, r) => setReverseTarget({ po: p, receipt: r })} />
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3">
                      <PoInvoicesCell po={po} onChanged={load}
                        onAdd={(p) => setInvDialog({ po: p, invoice: null })}
                        onEdit={(p, inv) => setInvDialog({ po: p, invoice: inv })} />
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3"><PoPriorityCell po={po} onChanged={load} /></td>
                    <td className="border-l border-slate-100 px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>{rowActions(po)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {showMoreBar}
        </div>
      )}

      <PoDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} initial={dialog.initial}
        suppliers={suppliers} components={components} destinations={destinations} onSaved={load} />
      <PoShipmentDialog target={shipDialog} onOpenChange={(o) => { if (!o) setShipDialog(null); }} onSaved={load} />
      <PoReceiveDialog target={recvDialog} onOpenChange={(o) => { if (!o) setRecvDialog(null); }} onSaved={load} />
      {invDialog && <PoInvoiceDialog ctx={invDialog} onClose={() => setInvDialog(null)} onSaved={load} />}
      {invReview && (
        <InvoiceReviewDialog parsed={invReview} pos={pos} suppliers={suppliers} components={components}
          onClose={() => setInvReview(null)} onSaved={load} />
      )}

      <AlertDialog open={!!delPo} onOpenChange={(o) => { if (!o) setDelPo(null); }}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {delPo?.po_number}?</AlertDialogTitle>
            <AlertDialogDescription>This removes the purchase order, its shipments, and its On Order quantities. Orders with posted receipts cannot be deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-sm bg-red-600 text-white hover:bg-red-700" data-testid="po-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!closeShortPo} onOpenChange={(o) => { if (!o) setCloseShortPo(null); }}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Close {closeShortPo?.po_number} short?</AlertDialogTitle>
            <AlertDialogDescription>
              The unreceived quantities will be removed from On Order and the PO will be marked Received with a shortage on record. Received history is preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCloseShort} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="po-close-short-confirm">Close Short</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!reverseTarget} onOpenChange={(o) => { if (!o) setReverseTarget(null); }}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Reverse {reverseTarget?.receipt?.receipt_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              The accepted quantities from this receiving report will be removed from inventory and put back as outstanding on {reverseTarget?.po?.po_number}. A reversal entry is recorded in inventory history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReverse} className="rounded-sm bg-red-600 text-white hover:bg-red-700" data-testid="po-reverse-confirm">Reverse Receipt</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
